/* One reader for "what does this page state", replacing five for "what does
 * this LAYOUT state".
 *
 * Every layout this engine has met is a different visual encoding of the same
 * relation — (label, value, scope). Six encodings, one relation:
 *
 *     BUSINESS DESCRIPTION      Charter School        column padding
 *     Business Description:     Charter School        inline colon
 *     | Business Description | Charter School |       markdown cell
 *     DESCRIPTION OF BUSINESS
 *     Business Description: ...                       stacked
 *     Item 2. Business of the Named Insured is: ...   numbered
 *     001 001 Building            $5,000,000          grid row
 *
 * A carrier can invent a new ENCODING easily; a new relation almost never. So
 * a reader written against the relation generalises where five written against
 * encodings do not — which is the difference between a client needing code and
 * a client needing a fixture.
 *
 * WHAT THIS DOES NOT DO. It does not decide what a value means. It reports the
 * position a value was found in and leaves the judgement to the caller, which
 * is the whole point: every wrong number this engine has produced was a real
 * number read from the wrong POSITION — a tax-matters sublimit under another
 * coverage part, a policy number, a deductible, a statutory guaranty cap. Each
 * was near the right words. None was in the right place.
 *
 * Input is expected to be spTableLines-normalised, so markdown pipes and HTML
 * table rows have already collapsed to one line per printed row.
 */
(function (root) {
  /* A THOUSANDS SEPARATOR THAT CAME BACK WITH A SPACE AFTER IT.
   *
   * LlamaParse renders some declarations cells as "$1, 000, 000". The money
   * pattern stops at the space, so the value read as "$1," — and a truncated
   * amount is published as confidently as a whole one. Tulsa Honor Academy's
   * abuse and molestation limits came back "$1," each act and "$2,"
   * aggregate, against a policy stating $1,000,000 and $2,000,000.
   *
   * Only a comma between a digit and exactly three more digits is closed up,
   * so a list ("Item 1, 2 and 3") and a date ("May 1, 2005") are untouched:
   * neither has three digits following.
   */
  const REJOIN_DIGIT_GROUPS = /(\d),\s+(?=\d{3}(?!\d))/g;

  const norm = t => {
    const RA = root.spRetroAnchor;
    const flat = RA && RA.spTableLines ? RA.spTableLines(t) : String(t || '');
    return flat.replace(REJOIN_DIGIT_GROUPS, '$1,');
  };

  // A heading opens a SCOPE: everything under it belongs to it until the next
  // one. This is what binds a limit to its coverage part without a character
  // window — the thing proximity was standing in for.
  const HEADING = /^[\s|]*([A-Z][A-Z0-9 &'\/().,-]{6,80})\s*$/;
  const HEADING_NOUN = /\b(COVERAGE|DECLARATIONS|SCHEDULE|PART|FORM|LIABILITY|PROPERTY|SECTION|ITEM|ENDORSEMENT|NOTICE)\b/;

  //: A value is money, a date, or text. The KIND comes from the label, never
  //: from the number: $5,000 is a limit or a deductible depending entirely on
  //: what it sits beside, and reading the number tells you nothing.
  const KIND = [
    // Plurals matter. "\bretention\b" does not match "Retentions:", which is
    // how every Chubb retention block typed as money — the word that says what
    // the figures are is the heading above them, and it is always plural.
    [/\bsub-?limits?\b/i,                                              'sublimit'],
    [/\b(?:limits?|sums? insured|amounts? of insurance|aggregate|each (?:claim|occurrence|accident)|combined single)\b/i, 'limit'],
    [/\b(?:retentions?|deductibles?|self-?insured|retained)\b/i,       'retention'],
    [/\b(?:premiums?|surcharges?|taxes?|tax|fees?|assessments?|commissions?|expense constant)\b/i, 'cost'],
    [/\b(?:dates?|periods?|effective|expiration|inception)\b/i,        'date'],
    [/\b(?:coinsurance|valuation|causes? of loss|construction|occupancy|forms?)\b/i, 'attribute'],
  ];
  function kindOf(label) {
    for (const [re, k] of KIND) if (re.test(label)) return k;
    return 'text';
  }

  //: Where the value was found, strongest first. A caller may REPORT anything
  //: here; only the first two may be believed as a primary figure.
  //:
  //:   field     a labelled field on a declarations or schedule page
  //:   row       a row of a repeating schedule, label in its own cell
  //:   clause    inside a sentence — the form describing cover, not stating it
  const CLAUSE = /\b(?:we will (?:not )?pay|shall (?:not )?(?:apply|exceed)|in an? (?:aggregate )?amount not to exceed|arising out of|caused by|means\b|is amended|does not apply|subject to the)\b/i;

  /* SOME REFUSALS CANNOT BE MADE FROM THE LINE.
   *
   * "Hospital, medical and surgical benefits   $500,000" is a labelled field,
   * in a field position, with a round number, under a heading. Every
   * line-level test passes it — and it is a statutory cap on what a guaranty
   * association would pay if the CARRIER became insolvent, printed identically
   * on every accident and health policy sold in the state.
   *
   * The thing that disqualifies it is not on the line. It is the document:
   * this page is a notice, and nothing on a notice is this insured's cover.
   * So the page has a kind too, and it is part of a value's provenance.
   *
   * The same applies to fraud warnings and policyholder notices, which are
   * printed on everything and describe nothing.
   */
  const DOC_NOTICE = new RegExp([
    'guaranty\\s+association', 'insurance\\s+protection\\s+association',
    'becomes\\s+financially\\s+unable\\s+to\\s+meet\\s+its\\s+obligations',
    'fraud\\s+warning', 'notice\\s+to\\s+policyholders?',
    'this\\s+notice\\s+does\\s+not\\s+change'
  ].join('|'), 'i');

  const MONEY = /\$\s*([\d][\d,]*(?:\.\d{2})?)/;

  /* A DECLINATION IS A VALUE, NOT AN ABSENCE.
   *
   *     Flood                  No Coverage   per Occurrence and in the Annual Aggregate
   *     Earthquake             No Coverage   per Occurrence and in the Annual Aggregate
   *     Equipment Breakdown    No Coverage   per Occurrence
   *     Wind Driven Precip.    $500,000      In the annual aggregate
   *
   * Tulsa Honor Academy's property policy. One table, one column, four rows.
   * The reader recognised $500,000 as a value and "No Coverage" as prose, so
   * the Equipment Breakdown row parsed with "No Coverage" as its LABEL and
   * "per Occurrence" as its value — and downstream the coverage was reported
   * as present with limits that failed to parse, on a policy that declines it
   * three times over and attaches CC EQB 004, an Equipment Breakdown Exclusion.
   *
   * The carrier answered the question. It said no. That is a stronger fact
   * than a limit, and it was the one piece of the row nothing could read.
   *
   * These tokens are a closed set, the way the field LABELS turned out to be:
   * layouts vary freely between carriers, the vocabulary for "we did not sell
   * you this" does not.
   */
  const DECLINED = /^(?:no\s+coverage|not\s+covered|not\s+purchased|not\s+insured|excluded|exclusion|declined|nil|none|n\/?a|\$?\s*0(?:\.00)?)$/i;
  const ITEM_MARKER = /^\s*(?:item\s+\d+[.)]?|\([A-Za-z0-9]{1,3}\)|\d{1,2}[.)])\s*/i;

  const clean = s => String(s || '').replace(/\s+/g, ' ').trim();

  /* A grid row's leading index columns are not part of its label.
   *
   * spTableLines collapses "| 001 | 001 | Building | $5,000,000 |" to
   * "001 001 Building $5,000,000" with single spaces, so the column rule
   * cannot see the cells and the whole prefix lands in the label. Asking for
   * "Building" then misses it — which it did, on the one fixture written in
   * the shape production actually receives.
   */
  const stripIndex = s => clean(String(s || '').replace(/^(?:\s*\d{1,4}\s+){1,4}/, ''));

  /** Every (label, value) a page states, with where it was found.
   *
   *  Returns [{label, value, kind, provenance, scope, line, index}]. */
  /* THE SAME PAGE, PARSED EIGHTY TIMES.
   *
   * Reading a field is cheap; reading every field on a page is not, and
   * nothing here remembered having done it. Twelve limit specs ask each
   * document, the stance reader asks once per coverage in the vocabulary, and
   * the property migration asks again — on Tulsa Honor Academy's four policies
   * that is 82 calls parsing 19.7 million characters of a 0.97 million
   * character program, twenty times over.
   *
   * In Node it reads as a slow report. On a browser's main thread it reads as
   * the tab freezing, which is what a user saw.
   *
   * Keyed by the text itself, because pages arrive as objects that are rebuilt
   * between passes and the text is what is actually shared. The cache is
   * capped: a policy read as 277 recovered pages would otherwise hold every
   * one of them for the life of the session.
   *
   * The returned array is shared, so callers must not mutate it. Every caller
   * filters or iterates, which copies.
   */
  const _FIELDS_CACHE = new Map();
  const _FIELDS_CACHE_MAX = 400;

  function spReadFields(text) {
    const key = String(text == null ? '' : text);
    if (!key) return [];
    const hit = _FIELDS_CACHE.get(key);
    if (hit) return hit;
    const out = spReadFieldsUncached(key);
    if (_FIELDS_CACHE.size >= _FIELDS_CACHE_MAX) {
      _FIELDS_CACHE.delete(_FIELDS_CACHE.keys().next().value);
    }
    _FIELDS_CACHE.set(key, out);
    return out;
  }

  function spReadFieldsUncached(text) {
    const body0 = norm(text);
    const lines = body0.split('\n');
    const out = [];
    let scope = null;
    /* A PAGE THAT MENTIONS A NOTICE IS NOT A NOTICE.
     *
     * Read as "contains a notice phrase", this marked a surplus-lines
     * DECLARATIONS page as a notice — it carries a policyholder-notice
     * paragraph at the foot, as most declarations pages do — and every sum
     * insured on it became unbelievable. A $26,582,592 building limit was read
     * correctly and then refused.
     *
     * A notice is a page that is ONLY a notice. So it needs the markers AND
     * the absence of what a declarations page always has: a limit label, a
     * coverage part title, a schedule. The asymmetry is deliberate — refusing
     * a real limit is the expensive direction, so the bar for calling a page a
     * notice is the higher one.
     */
    const DECLARATIVE = /\b(?:limits?\s+of\s+(?:liability|insurance)|coverage\s+part|declarations|schedule\s+of\s+(?:values|coverages|premises)|sums?\s+insured|policy\s+limit)\b/i;
    const isNotice = DOC_NOTICE.test(body0) && !DECLARATIVE.test(body0);
    /* IN A GRID, THE KIND IS IN THE COLUMN HEADER, NOT THE ROW LABEL.
     *
     * "001  001  Building  $5,000,000" has no word in it that says what the
     * number is. The header two lines above does: "Prem. No. Bldg. No.
     * Coverage Limit Of Insurance". A reader that types a value from its row
     * label alone calls every grid figure "text" — which is how the first run
     * of this reader found the building limit in two layouts out of five.
     *
     * The header is remembered until the next heading, which is the same scope
     * rule applied one level down.
     */
    let columnKind = null;
    let lastLabel = null;

    lines.forEach((raw, i) => {
      /* A CURRENCY SIGN SEPARATED FROM ITS DIGITS IS STILL ONE AMOUNT.
       *
       * GuideOne right-aligns the number and left-aligns the sign:
       *
       *     Each Occurrence Limit                    $     1,000,000
       *
       * Cells split on two or more spaces, so that row arrived as three cells
       * — the label, a lone "$", and a bare number — and neither of the last
       * two reads as money. Matrix Head Start's whole GL declarations page was
       * invisible, and its Each Occurrence limit was reported as $3,000,000,
       * which is the general aggregate one line above.
       */
      const line = raw.replace(/\s+$/, '').replace(/\$\s{2,}(?=[\d])/g, '$');
      if (!line.trim()) return;

      // A heading changes scope and states nothing itself.
      const h = HEADING.exec(line);
      if (h && HEADING_NOUN.test(h[1]) && !MONEY.test(line)) {
        scope = clean(h[1]); columnKind = null; return;
      }

      // A header ROW: several cells, none of them money, one of them naming a
      // kind. It states nothing itself and types every row beneath it.
      if (!MONEY.test(line)) {
        const head = line.split(/\s{2,}/).map(clean).filter(Boolean);
        // A row that states a declination is a row, however much its trailing
        // cell reads like a column header. "Flood  No Coverage  per Occurrence
        // and in the Annual Aggregate" ends in the word "Aggregate", and was
        // being consumed as a header that types the rows beneath it — so the
        // policy's answer on Flood and Earthquake disappeared entirely, while
        // Equipment Breakdown survived only because its trailing cell happened
        // to say "per Occurrence" instead.
        if (head.length >= 2 && !head.some(c => DECLINED.test(c))) {
          const named = head.map(kindOf).find(k => k === 'limit' || k === 'retention' || k === 'cost');
          if (named) { columnKind = named; return; }
        }
      }

      const provenance = isNotice ? 'notice' : (CLAUSE.test(line) ? 'clause' : 'field');
      const body = line.replace(ITEM_MARKER, '');

      // 0. A label alone, ending in a colon — "(C) Retentions:", "Deductibles:"
      //    That is a GROUP HEADER: it states nothing and types everything under
      //    it until the next heading. Without it, "Each Employment Claim: $35,000"
      //    under "Retentions:" typed as money, because the word that says what
      //    the figure is sits on the line above and nothing was reading it.
      const groupHead = /^([A-Za-z][A-Za-z '\/&()-]{3,40})\s*:\s*$/.exec(body);
      if (groupHead) {
        const k = kindOf(groupHead[1]);
        if (k !== 'text') { columnKind = k; return; }
      }

      // 1. Inline colon — "Business Description: Charter School"
      const colon = /^([^:|]{2,60}?)\s*:\s*(.+)$/.exec(body);
      if (colon && !/^\s*$/.test(colon[2])) {
        push(clean(colon[1]), clean(colon[2]), provenance, i);
        return;
      }

      /* THE AMOUNT AND ITS KIND CAN SHARE ONE CELL.
       *
       *     Employee Benefits Programs   $1,000,000 each   $0 each   $174
       *                                  $2,000,000 aggregate
       *
       * One space, not two, so the continuation row is a single cell and no
       * column rule reaches it. Tulsa Honor Academy's employee benefits
       * aggregate was reported as $3,000,000; the schedule says $2,000,000,
       * on the line nothing read.
       */
      const solo = /^(\$\s*[\d][\d,]*(?:\.\d{2})?)\s+((?:aggregate|each\s+\S+|per\s+\S+)[^$]{0,24})$/i
        .exec(body.trim());
      if (solo && lastLabel) {
        push(lastLabel + ' ' + clean(solo[2]), solo[1], 'row', i);
        return;
      }

      // 2. Columns — two or more spaces separate the cells of one printed row.
      //    After spTableLines a markdown or HTML table looks exactly like this,
      //    which is why one rule covers both.
      const cells = body.split(/\s{2,}/).map(clean).filter(Boolean);
      if (cells.length >= 2) {
        // A grid row may carry index columns before the label: "001 001
        // Building  $5,000,000". The label is the last cell that is not a bare
        // number and not money; the value is the first money cell after it,
        // or the last cell.
        // A declination token sits in the value column, so it marks the value
        // position exactly as an amount does. Without this the label lands on
        // "No Coverage" and the value on whatever trails it.
        const moneyAt = cells.findIndex(c => MONEY.test(c) || DECLINED.test(c));
        const labelIdx = (moneyAt > 0 ? moneyAt - 1 : cells.length - 2);
        // A symbols column is not a label. Business auto declarations print
        // "Covered Autos Liability   8,9   $1,000,000   $1,874", and the
        // covered-auto symbols were becoming the label of the limit.
        const label = cells.slice(0, labelIdx + 1)
          .filter(c => !/^[\d,\s]+$/.test(c)).pop();
        const value = moneyAt >= 0 ? cells[moneyAt] : cells[cells.length - 1];
        /* THE NAME OF THE LIMIT CAN SIT AFTER THE AMOUNT, AND THE NEXT ROW
         * CAN OMIT THE LABEL ENTIRELY.
         *
         *     Abuse Or Molestation Liability    $1,000,000    Each Act Limit
         *                                       $2,000,000    Aggregate Limit
         *     Special Supplementary Payment Limit    $10,000
         *
         * Tulsa Honor Academy's abuse schedule. The first row's label says
         * which COVERAGE; the trailing cell says which LIMIT. The second row
         * says only the amount and the kind, and belongs to the row above it.
         * Read plainly, the aggregate row vanished and the each-act limit was
         * reported as $2,000,000 — the aggregate, from a row nothing read.
         *
         * The qualified label is emitted ALONGSIDE the plain one, never
         * instead of it: "Building  $26,582,592  per Occurrence" must still
         * answer to "Building".
         */
        const after = moneyAt >= 0 ? cells.slice(moneyAt + 1).find(Boolean) : null;
        const qualifier = after && after.length <= 40
          && /\b(?:aggregate|each\s+\w+|per\s+\w+|any\s+one\s+\w+|limit)\b/i.test(after)
          ? clean(after) : null;
        if (moneyAt === 0 && qualifier) {
          // With no label above it, the descriptor IS the label: a schedule
          // that reads "$1,000,000  Each Wrongful Act" over "$3,000,000
          // Aggregate" states two limits and names them, and nothing else on
          // the page names them at all.
          push((lastLabel ? lastLabel + ' ' : '') + qualifier, cells[0], 'row', i);
          return;
        }
        if (label && value && label !== value) {
          push(label, value, provenance === 'field' ? 'row' : provenance, i);
          lastLabel = label;
          if (qualifier) push(label + ' ' + qualifier, value, 'row', i);
          return;
        }
      }

      // 2b. A label and its money with only one space between them —
      //     "Building $ 5,000,000". The shortest property page there is, and
      //     the column rule cannot see it because there is no column.
      /* MATCHED FROM THE END, BECAUSE THE FRONT IS UNBOUNDED.
       *
       * This was `^(.*?[A-Za-z].*?)\s*($...)\s*$` — two lazy runs before an
       * end anchor. On a line that matches, it is fine. On a line that does
       * NOT, the engine tries every split of the prefix, which is quadratic in
       * the length of the line.
       *
       * Policy text out of pdftotext has short lines and it never showed. The
       * product does not read pdftotext: LlamaParse returns markdown TABLES,
       * and one table row is one line ten thousand characters long. Three
       * seconds per page, on the browser's main thread, for a document with
       * dozens of such pages — the tab stops responding, which is what a user
       * reported and what no test here could have caught, because every
       * fixture in this repository has short lines.
       *
       * Finding the amount first and taking the prefix as whatever precedes it
       * does the same job in one pass.
       */
      const tailMoney = /(\$\s*[\d][\d,]*(?:\.\d{2})?)\s*$/.exec(body);
      if (tailMoney) {
        const head = body.slice(0, tailMoney.index);
        if (/[A-Za-z]/.test(head) && clean(head).length >= 3) {
          push(stripIndex(head), clean(tailMoney[1]), provenance, i);
          return;
        }
      }

      // 3. Stacked — a label alone, its value on the next non-empty line. Only
      //    when the next line is not itself a label, or a heading would eat its
      //    own value.
      if (/^[A-Za-z][A-Za-z ,'\/&().-]{4,60}$/.test(body.trim())) {
        const next = (lines[i + 1] || '').trim();
        if (next && !/[:|]/.test(next) && !HEADING.test(next)) {
          push(clean(body), clean(next), provenance, i);
        }
      }
    });

    function push(label, value, provenance, line) {
      if (!label || !value) return;
      if (value.length > 90) return;
      /* A CLAUSE IS ALLOWED A LONG LABEL, BECAUSE A CLAUSE IS A SENTENCE.
       *
       * The cap exists so a run-on line cannot masquerade as a field. Applied
       * to clauses it did the opposite of what this module promises: "the
       * exclusion shall not apply to Defense Costs in an aggregate amount not
       * to exceed $100,000" has a 78-character label, so it was dropped
       * entirely — and a figure the caller must be able to SEE and refuse
       * became a figure the caller never hears about.
       *
       * Reportable and not believable is the contract. Silently dropped is
       * neither.
       */
      const cap = provenance === 'clause' ? 200 : 70;
      if (label.length > cap) return;
      const own = kindOf(label);
      // The row label wins when it says something; the column header fills the
      // silence. "Deductible $5,000" under a Limit Of Insurance header is a
      // deductible.
      /* Money with nothing to type it is 'money', not 'text'.
       *
       * The reader reports the POSITION a value was found in and leaves the
       * judgement to the caller — but the type has to be uniform across
       * layouts or the caller needs a different query per layout, which is the
       * problem this module exists to remove. A figure nobody could type is
       * still visibly a figure. */
      const declined = DECLINED.test(clean(value));
      const kind = own !== 'text' ? own
        : (MONEY.test(value) ? (columnKind || 'money')
        : (declined ? (columnKind || 'limit') : own));
      out.push({ label, value, kind, provenance, scope, line,
                 declined,
                 money: (MONEY.exec(value) || [])[1] || null });
    }
    return out;
  }

  /* FIELDS READ FROM A GRID, WITH THE THREE FACTS KEPT APART.
   *
   * The line-based reader gets the amount right and then smears everything
   * else together, because after flattening there is nothing left to tell a
   * label from a qualifier. Measured on one four-policy program: twelve
   * amounts read twice with overlapping labels, the difference between the
   * two being the qualifier —
   *
   *     $26,582,592  "Building"  and  "Building per Occurrence"
   *     $1,000,000   "Abuse Or Molestation Liability"
   *                  and "Abuse Or Molestation Liability Each Act Limit"
   *
   * — four labels that are sentence fragments from a wrapped cell, and 54 of
   * 138 amounts with no coverage scope, because a schedule page carries a
   * caption where a declarations page carries a heading.
   *
   * A grid answers all three separately: the label is the cell to the LEFT of
   * the amount, the qualifier is what follows it on the row (or its column
   * header), and the scope is the table's caption. Nothing is inferred from
   * distance.
   */
  /* ONE GRID, TURNED INTO FIELDS.
   *
   * Shared by both sources of grids: tables parsed out of markdown here, and
   * tables the PARSER already separated into rows and handed us. The second
   * is strictly better — it never passed through a flattening step — and the
   * logic for reading a row is identical either way, so it lives once.
   */
  function gridToFields(g, out, state) {
    const hdr = (g.header || []);
    const banner = hdr.length > 1 && hdr.every(h => h === hdr[0]);
    /* The banner and the caption are frequently the same words — a table
     * headed "LIMITS OF LIABILITY" under a heading of the same name — and
     * concatenating them gave a scope of "LIMITS OF LIABILITY LIMITS OF
     * LIABILITY". Joined only when each says something the other does not. */
    let caption;
    if (banner) {
      const b = clean(hdr[0]), c = clean(g.caption);
      caption = (c && c.toLowerCase().indexOf(b.toLowerCase()) < 0 && b.toLowerCase().indexOf(c.toLowerCase()) < 0)
        ? clean(b + ' ' + c) : (b || c);
    } else {
      caption = clean(g.caption);
    }
    const usable = c => c && c.length >= 8 && !MONEY.test(c)
      && /[A-Za-z]{4}/.test(c) && !/^(?:policy\s*#|page\b|form\b|\d)/i.test(c);
    const titleRow = (!((g.rows || []).some(r => r.some(c => MONEY.test(clean(c))))))
      ? clean((g.rows || []).map(r => r.filter(Boolean).join(' ')).join(' ')) : '';
    if (usable(caption)) state.lastCaption = caption;
    else if (usable(titleRow)) state.lastCaption = titleRow.slice(0, 120);
    if (!usable(caption)) caption = state.lastCaption;
    let carried = '';
    for (const row of (g.rows || [])) {
      const cells = row.map(clean);
      const moneyAt = cells.map((c, i) => (MONEY.test(c) || DECLINED.test(c)) ? i : -1).filter(i => i >= 0);
      if (!moneyAt.length) {
        const words = cells.filter(Boolean);
        if (words.length === 1 && words[0].length <= 60) carried = words[0];
        continue;
      }
      for (const mi of moneyAt) {
        let label = '';
        for (let k = mi - 1; k >= 0; k--) {
          const c = cells[k];
          if (!c || /^\d{1,3}[.)]?$/.test(c)) continue;
          /* KEEP LOOKING PAST ANOTHER AMOUNT.
           *
           * A row can state the same figure under several columns — one per
           * subscribing carrier, or Each Occurrence beside Aggregate:
           *
           *     ["Attachment Point", "$5,000,000", "$5,000,000"]
           *
           * Stopping at the first money cell left the SECOND amount with no
           * label, so it fell back to the group heading above the row and
           * published "Windstorm $5,000,000" — a layer boundary presented as
           * a windstorm limit. The label of every amount on a row is the
           * row's own header; what separates them is the column. */
          if (MONEY.test(c)) continue;
          // "1. Accounts Receivable" — the schedule's row number is furniture.
          label = c.replace(/^\s*\d{1,3}[.)]\s+/, '') || c; break;
        }
        if (!label) label = carried;
        if (!label) continue;
        const after = cells.slice(mi + 1).filter(c => c && !MONEY.test(c));
        let qualifier = after.length ? after.join(' ') : '';
        if (!qualifier && !banner && hdr[mi] && hdr[mi] !== label) qualifier = clean(hdr[mi]);
        if (qualifier.length > 90) qualifier = qualifier.slice(0, 90).trim();
        const value = cells[mi];
        out.push({
          label, value, qualifier: qualifier || null,
          kind: kindOf(label + ' ' + qualifier),
          // One provenance word for both grid sources. Giving parser-supplied
          // tables their own ('table') meant three believability tests, which
          // accept field/row/grid, silently rejected every limit read from
          // them — exactly how they rejected 'grid' when that was introduced.
          // The distinction that matters for diagnostics rides in `source`.
          provenance: 'grid', source: g.provenance || 'markdown-grid',
          scope: caption || null, money: MONEY.test(value), line: g.line || 0
        });
      }
    }
  }

  /* FIELDS FROM TABLES THE PARSER ALREADY SEPARATED.
   *
   * The backend can ask LlamaParse for its per-page result instead of one
   * markdown blob, and each table then arrives as rows of cells. Reading
   * those directly skips the whole flatten-and-rebuild round trip — which is
   * where a class of limits defect kept appearing, most recently a
   * pipe-to-spaces replace undone by a whitespace collapse on the next line,
   * which made every schedule row with a qualifier column unreadable.
   *
   * A table the parser is not confident it read (isPerfectTable false) is
   * skipped, and the markdown path covers that page instead: cells that were
   * guessed are worse than cells rebuilt from a layout we can see.
   */
  function spTableFields(tables) {
    const out = [];
    const state = { lastCaption: '' };
    for (const t of (tables || [])) {
      if (!t || !Array.isArray(t.rows) || !t.rows.length) continue;
      if (t.perfect === false) continue;
      // The parser gives no header/body split, so a first row with no amount
      // in it is the header — the same rule the markdown parser applies.
      /* A CELL STILL CARRIES THE PARSER'S MARKUP.
       *
       * spTableLines was quietly cleaning this on the markdown path — inline
       * tags, bold markers, entities — and rows arriving straight from the
       * parser never passed through it. Left raw, an umbrella schedule
       * published "$1,000,000 each Occurrence<br/>$3,000,000 General
       * Aggregate" as a label.
       *
       * A <br/> inside a cell is a line break, so it becomes a space: the
       * markdown path dropped the tag and ran the words together
       * ("each Occurrence$3,000,000"), which is worse than either. */
      const cleanCell = c => String(c == null ? '' : c)
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/?[a-z][^>]{0,40}>/gi, ' ')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/&#x([0-9a-f]+);/gi, (_m, h) => String.fromCharCode(parseInt(h, 16)))
        .replace(/&amp;/gi, '&').replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const rows = t.rows.map(r => (r || []).map(cleanCell));
      const headerish = rows.length > 1 && !rows[0].some(c => MONEY.test(clean(c)));
      gridToFields({
        caption: t.caption || '',
        header: headerish ? rows[0] : [],
        rows: headerish ? rows.slice(1) : rows,
        provenance: 'parser-tables',
        line: 0
      }, out, state);
    }
    return out;
  }

  function spGridFields(text) {
    const RA = root.spRetroAnchor;
    if (!RA || !RA.spTableGrid) return [];
    const out = [];
    const state = { lastCaption: '' };
    for (const g of RA.spTableGrid(text)) gridToFields(g, out, state);
    return out;
  }

  /* BOTH READERS, THE GRID FIRST.
   *
   * A grid gets the association right and only covers the parts of a page
   * that are tables; the line reader covers everything and gets association
   * right only where the layout is simple. So: take every grid field, then
   * every line field the grid did not already account for.
   *
   * "Already accounted for" is the same test that measured the defect — same
   * amount, and one label a prefix of the other, which is what a smeared
   * qualifier looks like ("Building" against "Building per Occurrence"). Two
   * coverages that genuinely share a round number have unrelated labels and
   * both survive.
   *
   * Measured across one four-policy program: duplicate readings 12 to 0, and
   * amounts with no coverage scope 54 of 138 down to 12 of 129.
   */
  function spAllFields(page) {
    /* A page, or just its text.
     *
     * Where the backend supplied tables the parser had already separated, use
     * those: they never went through a flattening step. Otherwise parse the
     * markdown, which is what the markdown endpoint and every local extractor
     * give us. The line reader then fills in whatever sits outside a table,
     * either way. */
    const isPage = page && typeof page === 'object';
    const text = isPage ? (page.text || page.md || '') : String(page == null ? '' : page);
    const supplied = isPage && Array.isArray(page.tables) && page.tables.length
      ? spTableFields(page.tables) : null;
    const grid = supplied && supplied.length ? supplied : spGridFields(text);
    if (!grid.length) return spReadFields(text);
    const covered = (f) => grid.some(g => {
      const a = String(g.label || ''), b = String(f.label || '');
      const overlap = a === b || (a && b && (a.indexOf(b) === 0 || b.indexOf(a) === 0));
      if (!overlap) return false;
      const gv = String(g.value), fv = String(f.value);
      if (gv === fv) return true;
      /* A FLATTENED COPY OF ROWS THE GRID ALREADY HAS.
       *
       * Where the parser merged two printed rows into one table row, its page
       * markdown carries the merge even after the grid has been taken apart:
       *
       *   grid  "Personal Property"  $1,102,500
       *         "Time Element"       $1,310,545
       *   line  "Personal Property Time Element"  "$1,102,500 $1,310,545"
       *
       * The line reading is not a second limit, it is the same two limits run
       * together. Keeping it left the property slot with two candidates for
       * business personal property and the uniqueness rule refused both, so a
       * limit the grid had read correctly went unpublished. A longer label
       * that starts with the grid's, over a value that CONTAINS the grid's,
       * is that merge and nothing else. */
      return fv.length > gv.length && fv.indexOf(gv) >= 0;
    });
    return grid.concat(spReadFields(text).filter(f => !covered(f)));
  }

  /** Fields matching a label, optionally within a scope and of a kind.
   *
   *  `within` is a substring of the scope heading, which is how a caller binds
   *  a figure to its coverage part without a character window. */
  function spFindField(fields, { label, within, kind, provenance } = {}) {
    const lab = label instanceof RegExp ? label : new RegExp(String(label || ''), 'i');
    return (fields || []).filter(f =>
      lab.test(f.label)
      && (!within || (f.scope && new RegExp(within, 'i').test(f.scope)))
      && (!kind || f.kind === kind)
      && (!provenance || f.provenance === provenance));
  }

  root.spReadFields = spReadFields;
  root.spGridFields = spGridFields;
  root.spTableFields = spTableFields;
  root.spAllFields = spAllFields;
  root.spFindField = spFindField;
})(typeof window !== 'undefined' ? window : globalThis);
